'use client';
import { SystemAttribute } from '../../../../types/attributes';
import { CmsProduct } from '../../../../types/cmsProduct';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../../utils/analytics';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getApplyButtonLabel } from '../../../../utils/helpers/router';
import { sessionAttributionTracker } from '../../../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { ApplyCtaLink } from '../../../ApplyCta';
import Button from '../../../Button';
import { CmsImage } from '../../../CmsImage';
import { useProductBlockAsideModal } from '../../../ProductBlockAsideModal';
import { hasPdpModalContent } from '../../../ProductPage/hasPdpModalContent';
import Tooltip from '../../../Tooltip';
import { TRANSLATIONS } from '../../i18n';
import { AioModuleTracking, GA_TITLE_PREFIX } from '../../tracking';
import { getSharedAttributeTooltip, getSharedAttributeValue } from './utils';

type ProductCardAioModuleProps = {
  /** Required: the module drops cards whose product relation is empty. */
  product: CmsProduct;
  /** The chip above the card, e.g. "Up to S$1,649 in rewards". Editor input. */
  highlightTag?: string;
  /** The one spec shown on every card of the module, chosen once per block. */
  sharedProductAttribute?: SystemAttribute;
  /** Card position in the module — GA `index` on the product events. */
  index: number;
} & AioModuleTracking;

export const ProductCardAioModule = ({
  product,
  highlightTag,
  sharedProductAttribute,
  index,
  pushDataLayer,
  componentIndex,
  slug,
}: ProductCardAioModuleProps): JSX.Element => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });
  const { openModal } = useProductBlockAsideModal();

  const sharedValue = getSharedAttributeValue(product, sharedProductAttribute);
  const tooltip = getSharedAttributeTooltip(product, sharedProductAttribute);

  const showViewDetails =
    Boolean(product.url_alias) && hasPdpModalContent(product);

  const highlight = highlightTag?.trim();

  /**
   * The three product events of CMS-1964 (#2534 tooltip hover, #2535 View
   * Details, #2536 Apply Now). All go through `sendProductAnalytics`, which
   * owns the `items[]` / `reward_campaign_*` / `product_context_parameter`
   * shape, so only what differs per item is spelled out here.
   *
   * `component_index` rides in `extraData`: the shared helper has no parameter
   * for it, and every other block passes it the same way. So does
   * `search_result_parameter`: the ticket asks for it blank when no search
   * criteria are involved, and a blog article has none.
   */
  const trackProduct = (
    event: string,
    action: UserActionEnum,
    componentType: ComponentTypeEnum,
    componentTitle: string,
  ) => {
    if (!pushDataLayer) return;

    sendProductAnalytics({
      event,
      action,
      componentType,
      componentTitle: `${GA_TITLE_PREFIX}/${componentTitle}`,
      itemListName: slug,
      product,
      // A string, as ImageCarousel and SimilarProducts send it.
      index: String(index),
      pushDataLayer,
      extraData: {
        component_index: componentIndex ?? '',
        search_result_parameter: '',
      },
    });
  };

  return (
    <article
      className="flex h-full flex-col gap-spacing/gap/soft-12px"
      data-component="aio-module-product-card"
      data-product-id={product.uid ?? product.entryUID}
    >
      {highlight && (
        <div className="rounded-radius/button px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-fill/primary-light">
          <div className="mhg-alpha-typography-bullet-2 text-fill/primary-dark">
            {highlight}
          </div>
        </div>
      )}

      <div className="mt-auto flex items-center gap-spacing/gap/x-sm">
        {product.product_image && (
          <CmsImage
            src={product.product_image}
            alt={product.product_name || 'Product image'}
            width={230}
            sizes={{ _: 230 }}
            height={127}
            loading="lazy"
            className="h-14 w-24 shrink-0 rounded-radius/button object-contain"
          />
        )}

        <div className="min-w-0 flex-1">
          <p className="mhg-alpha-typography-caption text-text/secondary-lighter">
            {product.product_name}
          </p>

          {sharedValue && (
            <div className="mt-spacing/padding/soft-4px flex items-center gap-spacing/gap/x-xs">
              <p className="mhg-alpha-typography-bullet-2 text-text/primary">
                {sharedValue}
              </p>
              {tooltip && (
                <Tooltip
                  placement="top"
                  infoClass="!size-[--shapes-icon-size-sm] text-text/disabled"
                  content={tooltip}
                  // #2534. The primitive reports a tap as a click; the ticket
                  // names only `hover`, so the trigger is passed through rather
                  // than forced - a tap is not a hover. The dynamic half is the
                  // attribute's label: the tooltip has no title of its own, and
                  // its body is a paragraph.
                  onOpen={(trigger) =>
                    trackProduct(
                      'product_interaction',
                      trigger === 'click'
                        ? UserActionEnum.CLICK
                        : UserActionEnum.HOVER,
                      ComponentTypeEnum.TOOLTIP,
                      `Tooltip_${sharedProductAttribute?.label ?? sharedProductAttribute?.name ?? ''}`,
                    )
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Flex, not a two-column grid: either button can be absent - View
          Details needs PDP content, and `ApplyCtaLink` renders nothing when the
          CTA url does not resolve - and `flex-1` lets whichever remains take
          the full width. A grid would leave the survivor at half width beside
          an empty column. */}
      <div className="flex gap-spacing/gap/x-xs">
        {showViewDetails && (
          <Button
            size="small"
            variant="outline"
            styleType="neutral-light-and-blue"
            className="flex-1"
            onClick={() => {
              // #2535
              trackProduct(
                'product_interaction',
                UserActionEnum.CLICK,
                ComponentTypeEnum.PRODUCT_CARD,
                'Button_View Details',
              );
              openModal(product.url_alias as string, null);
            }}
          >
            {t.aio_module_view_details}
          </Button>
        )}

        {/* <ApplyCtaLink> hides itself when the CTA url does not resolve, which
            is the "not sold on MH/SS/M101" case from the design annotation. */}
        <ApplyCtaLink
          product={product}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1"
        >
          <Button
            size="small"
            variant="outline"
            styleType="cta"
            className="w-full"
            onClick={() => {
              // Where Apply was clicked, for Reward Gateway lead attribution -
              // every other Apply button records it before its GA event.
              sessionAttributionTracker.recordApplyClick();
              // #2536, the conversion event. Title is static in the ticket even
              // though the button copy is authored - same as SimilarProducts.
              trackProduct(
                'product_click_apply',
                UserActionEnum.CLICK,
                ComponentTypeEnum.PRODUCT_CARD,
                'CTA_Apply Now',
              );
            }}
          >
            {getApplyButtonLabel(
              t.aio_module_apply_now,
              undefined,
              product.call_to_action,
            )}
          </Button>
        </ApplyCtaLink>
      </div>
    </article>
  );
};
